import { useState, FormEvent } from "react";

type FormStatus = "idle" | "submitting" | "success" | "error";

const brands = [
  { name: "Mercedes-Benz", abbr: "MERCEDES-BENZ" },
  { name: "BMW", abbr: "BMW" },
  { name: "Audi", abbr: "AUDI" },
  { name: "Volkswagen", abbr: "VOLKSWAGEN" },
  { name: "Porsche", abbr: "PORSCHE" },
  { name: "Jaguar", abbr: "JAGUAR" },
  { name: "Land Rover", abbr: "LAND ROVER" },
];

export default function ContactSection() {
  const [status, setStatus] = useState<FormStatus>("idle");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    if (textarea && textarea.value.length > 500) return;
    setStatus("submitting");
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch("https://readdy.ai/api/form/d7c8k3gr4h7u93gnq4hg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
      }
    } catch {
      setStatus("error");
    }
  };

  return (
    <section id="contact" style={{ background: "#ffffff" }}>

      {/* ── Brand Authority Row ── */}
      <div
        className="py-14 px-6"
        style={{ borderTop: "1px solid rgba(0,0,0,0.06)", borderBottom: "1px solid rgba(0,0,0,0.06)", background: "#F4F5F7" }}
      >
        <div className="max-w-6xl mx-auto">
          <p className="text-xs tracking-widest uppercase text-center mb-10 font-medium" style={{ color: "rgba(90,106,126,0.5)", letterSpacing: "0.25em" }}>
            European Brand Coverage
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {brands.map((brand) => (
              <div
                key={brand.name}
                className="flex items-center justify-center px-5 py-3 rounded-xl cursor-default transition-all duration-300 bg-white"
                style={{ border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 1px 6px rgba(0,0,0,0.04)" }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.borderColor = "rgba(245,158,11,0.4)";
                  el.style.boxShadow = "0 4px 16px rgba(217,119,6,0.1)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLDivElement;
                  el.style.borderColor = "rgba(0,0,0,0.07)";
                  el.style.boxShadow = "0 1px 6px rgba(0,0,0,0.04)";
                }}
                title={brand.name}
              >
                <span className="font-bold tracking-widest text-xs whitespace-nowrap" style={{ color: "rgba(30,45,65,0.45)", letterSpacing: "0.12em" }}>
                  {brand.abbr}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Contact Form ── */}
      <div className="py-28 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

            {/* Left: Info */}
            <div>
              <p className="text-xs tracking-widest uppercase mb-4 font-medium" style={{ color: "#D97706", letterSpacing: "0.25em" }}>
                B2B Inquiries
              </p>
              <h2 className="text-4xl md:text-5xl font-thin mb-4" style={{ letterSpacing: "0.04em", color: "#1a2332" }}>
                START YOUR
                <br />
                INQUIRY.
              </h2>
              <div className="mb-8" style={{ width: "60px", height: "2px", background: "#F59E0B" }} />
              <p className="text-base font-light leading-relaxed mb-12" style={{ color: "#5a6a7e", lineHeight: "1.85" }}>
                Submit your OE part numbers and sourcing requirements. Our team responds
                within 12 business hours with pricing, availability, and MOQ data.
              </p>

              {/* Contact Details */}
              <div className="flex flex-col gap-5 mb-10">
                <a href="mailto:chirunnexa@gmail.com" className="flex items-center gap-4 group cursor-pointer">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                    style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}
                  >
                    <i className="ri-mail-line text-lg" style={{ color: "#D97706" }} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(90,106,126,0.5)", letterSpacing: "0.15em" }}>
                      Email
                    </p>
                    <p className="text-sm font-medium transition-colors duration-200 group-hover:text-amber-600" style={{ color: "#1a2332" }}>
                      chirunnexa@gmail.com
                    </p>
                  </div>
                </a>

                <div className="flex items-center gap-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                    style={{ background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)" }}
                  >
                    <i className="ri-whatsapp-line text-lg" style={{ color: "#D97706" }} />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest mb-1" style={{ color: "rgba(90,106,126,0.5)", letterSpacing: "0.15em" }}>
                      Global WhatsApp
                    </p>
                    <a
                      href="https://wa.me/8613522126700"
                      className="text-sm font-medium transition-colors duration-200 hover:text-amber-600 block"
                      style={{ color: "#1a2332" }}
                      rel="nofollow"
                      target="_blank"
                    >
                      WhatsApp: +86 13522126700 (Text Only)
                    </a>
                    <p className="text-xs mt-0.5 font-light" style={{ color: "rgba(90,106,126,0.45)", fontSize: "10px" }}>
                      TEXT INQUIRIES ONLY — No voice calls
                    </p>
                  </div>
                </div>
              </div>

              {/* SLA box */}
              <div
                className="p-5 rounded-xl bg-white"
                style={{ border: "1px solid rgba(245,158,11,0.2)", boxShadow: "0 2px 12px rgba(217,119,6,0.06)" }}
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ background: "#F59E0B" }} />
                  <span className="text-xs uppercase tracking-widest font-medium" style={{ color: "#D97706", letterSpacing: "0.15em" }}>
                    Response SLA
                  </span>
                </div>
                <p className="text-sm" style={{ color: "#5a6a7e" }}>
                  All B2B inquiries acknowledged within{" "}
                  <strong style={{ color: "#1a2332" }}>12 business hours</strong>.
                  Pricing sheets delivered within{" "}
                  <strong style={{ color: "#1a2332" }}>48 hours</strong>.
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div
              className="rounded-2xl p-8 md:p-10 bg-white"
              style={{ border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}
            >
              {status === "success" ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div
                    className="w-16 h-16 flex items-center justify-center rounded-full mb-6"
                    style={{ background: "rgba(245,158,11,0.1)", border: "2px solid rgba(245,158,11,0.4)" }}
                  >
                    <i className="ri-check-line text-2xl" style={{ color: "#D97706" }} />
                  </div>
                  <h3 className="text-xl font-light mb-3" style={{ color: "#1a2332" }}>Inquiry Submitted</h3>
                  <p className="text-sm" style={{ color: "#5a6a7e" }}>
                    Our team will respond within 12 business hours.
                  </p>
                  <button
                    onClick={() => setStatus("idle")}
                    className="mt-8 px-6 py-2 rounded-lg text-sm font-medium transition-all hover:brightness-110 whitespace-nowrap cursor-pointer"
                    style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", color: "#D97706" }}
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <form
                  data-readdy-form
                  id="chirunnexa-b2b-inquiry"
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-6"
                >
                  {[
                    { label: "Full Name *", name: "fullName", type: "text", placeholder: "John Smith" },
                    { label: "Company Name *", name: "companyName", type: "text", placeholder: "Your Company Ltd." },
                    { label: "Email Address *", name: "email", type: "email", placeholder: "you@company.com" },
                  ].map((field) => (
                    <div key={field.name}>
                      <label className="block text-xs uppercase tracking-widest mb-2 font-medium" style={{ color: "rgba(90,106,126,0.65)", letterSpacing: "0.15em" }}>
                        {field.label}
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        required
                        placeholder={field.placeholder}
                        className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200"
                        style={{ background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1a2332", fontSize: "0.875rem" }}
                        onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.55)")}
                        onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")}
                      />
                    </div>
                  ))}

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs uppercase tracking-widest font-medium" style={{ color: "rgba(90,106,126,0.65)", letterSpacing: "0.15em" }}>
                        OE Part Numbers Needed *
                      </label>
                      <span className="text-xs" style={{ color: charCount > 480 ? "#ef4444" : "rgba(90,106,126,0.4)" }}>
                        {charCount}/500
                      </span>
                    </div>
                    <textarea
                      name="oePartNumbers"
                      required
                      rows={5}
                      maxLength={500}
                      placeholder={"e.g., 11287628650, A0001234567, 7L0698151B\nList part numbers, quantities, and vehicle applications..."}
                      className="w-full px-4 py-3 rounded-lg text-sm outline-none transition-all duration-200 resize-none"
                      style={{ background: "#F8F9FA", border: "1px solid rgba(0,0,0,0.1)", color: "#1a2332", fontSize: "0.875rem" }}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      onFocus={(e) => (e.currentTarget.style.borderColor = "rgba(245,158,11,0.55)")}
                      onBlur={(e) => (e.currentTarget.style.borderColor = "rgba(0,0,0,0.1)")}
                    />
                  </div>

                  {status === "error" && (
                    <p className="text-xs text-red-500">
                      Submission failed. Please try again or email us directly.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting" || charCount > 500}
                    className="w-full py-4 rounded-xl font-bold text-xs tracking-widest transition-all duration-200 hover:brightness-110 active:scale-95 disabled:opacity-50 whitespace-nowrap cursor-pointer text-white"
                    style={{ background: "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", letterSpacing: "0.15em" }}
                  >
                    {status === "submitting" ? "SUBMITTING..." : "SUBMIT INQUIRY"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
